import * as React from 'react';

import { StorageKeys } from '@/lib/persistence';

export interface DemoModeFilters {
  /**
   * Include tag groups -- OR-of-AND. Each entry is a chip; separate chips are
   * ORed, and a chip joined with "&" ANDs its tags (e.g. "nav&parking" ->
   * nav AND parking). Defaults to ["is_demo"] as a convenience example, but it
   * is fully removable -- nothing here is forced.
   */
  tags: string[];
  /** Excluded tags -- a scenario carrying any of these is hidden. */
  excludeTags: string[];
  /** Include name groups -- OR-of-AND, same "&" semantics as tags. */
  searchTerms: string[];
  /** Negative search terms -- the scenario name must not contain any term. */
  excludeSearchTerms: string[];
  /** Author include filter -- Auth0 user ids; a scenario by any of them matches. */
  userIds: string[];
  /** Author exclude filter -- scenarios by any of these authors are hidden. */
  excludeUserIds: string[];
  /** Hidden scenarios -- exact scenario names dropped from the view (hide button). */
  hiddenScenarios: string[];
  /** Hidden suites -- full suite tags (e.g. "suite:Foo") excluded via exclude_tags. */
  hiddenSuites: string[];
  /** Hidden suite runs -- full suite-run tags (e.g. "suite_run:Bar") excluded via exclude_tags. */
  hiddenSuiteRuns: string[];
}

interface DemoModeState {
  enabled: boolean;
  filters: DemoModeFilters;
}

interface DemoModeContextValue {
  enabled: boolean;
  filters: DemoModeFilters;
  setEnabled: (enabled: boolean) => void;
  updateFilters: (filters: Partial<DemoModeFilters>) => void;
  resetFilters: () => void;
}

const DemoModeContext = React.createContext<DemoModeContextValue | undefined>(undefined);

const DEFAULT_FILTERS: DemoModeFilters = {
  tags: ['is_demo'],
  excludeTags: [],
  searchTerms: [],
  excludeSearchTerms: [],
  userIds: [],
  excludeUserIds: [],
  hiddenScenarios: [],
  hiddenSuites: [],
  hiddenSuiteRuns: [],
};

function isStringArray(v: unknown): boolean {
  return Array.isArray(v) && v.every((item) => typeof item === 'string');
}

function isValidFilters(v: unknown): v is Partial<DemoModeFilters> {
  if (typeof v !== 'object' || v === null) return false;
  const obj = v as Record<string, unknown>;
  for (const key of [
    'tags',
    'excludeTags',
    'searchTerms',
    'excludeSearchTerms',
    'userIds',
    'excludeUserIds',
    'hiddenScenarios',
    'hiddenSuites',
    'hiddenSuiteRuns',
  ] as const) {
    if (key in obj && !isStringArray(obj[key])) return false;
  }
  // Legacy single-author field (migrated in migrateStoredFilters).
  if ('userId' in obj && obj.userId !== null && typeof obj.userId !== 'string') return false;
  return true;
}

/**
 * Reconcile a stored filter blob with the current shape. Migrates the legacy
 * single ``userId`` into ``userIds`` so existing users don't lose their author
 * filter. The legacy ``is_demo`` default tag carries over untouched -- it is a
 * removable include-tag chip, not a forced constraint.
 */
function migrateStoredFilters(stored: Partial<DemoModeFilters> & { userId?: string | null }): DemoModeFilters {
  const merged = { ...DEFAULT_FILTERS, ...stored };
  if ((!merged.userIds || merged.userIds.length === 0) && typeof stored.userId === 'string') {
    merged.userIds = [stored.userId];
  }
  return merged;
}

function getStoredState(): Partial<DemoModeState> {
  if (typeof window === 'undefined') return {};

  try {
    const raw = localStorage.getItem(StorageKeys.DEMO_MODE);
    if (!raw) return {};
    const parsed: unknown = JSON.parse(raw);
    if (typeof parsed !== 'object' || parsed === null) return {};
    const obj = parsed as Record<string, unknown>;
    const result: Partial<DemoModeState> = {};
    if (typeof obj.enabled === 'boolean') result.enabled = obj.enabled;
    if (isValidFilters(obj.filters)) result.filters = migrateStoredFilters(obj.filters);
    return result;
  } catch {
    return {};
  }
}

function saveState(state: DemoModeState): void {
  if (typeof window === 'undefined') return;

  try {
    localStorage.setItem(StorageKeys.DEMO_MODE, JSON.stringify(state));
  } catch {
    // Storage full or unavailable
  }
}

function initializeState(): DemoModeState {
  const stored = getStoredState();
  return {
    enabled: stored.enabled ?? false,
    filters: stored.filters ?? { ...DEFAULT_FILTERS },
  };
}

export function DemoModeProvider({ children }: { children: React.ReactNode }) {
  const [state, setState] = React.useState<DemoModeState>(initializeState);

  const setEnabled = React.useCallback((enabled: boolean) => {
    setState((prev) => {
      const next = { ...prev, enabled };
      saveState(next);
      return next;
    });
  }, []);

  const updateFilters = React.useCallback((partial: Partial<DemoModeFilters>) => {
    setState((prev) => {
      const next = { ...prev, filters: { ...prev.filters, ...partial } };
      saveState(next);
      return next;
    });
  }, []);

  const resetFilters = React.useCallback(() => {
    setState((prev) => {
      const next = { ...prev, filters: { ...DEFAULT_FILTERS } };
      saveState(next);
      return next;
    });
  }, []);

  const value = React.useMemo(
    () => ({
      enabled: state.enabled,
      filters: state.filters,
      setEnabled,
      updateFilters,
      resetFilters,
    }),
    [state.enabled, state.filters, setEnabled, updateFilters, resetFilters]
  );

  return <DemoModeContext.Provider value={value}>{children}</DemoModeContext.Provider>;
}

export function useDemoMode() {
  const context = React.useContext(DemoModeContext);
  if (context === undefined) {
    throw new Error('useDemoMode must be used within a DemoModeProvider');
  }
  return context;
}
